/*
 * ******************************************************************
 * Copyright (c) 2022-2026 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */
import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output, QueryList, ViewChild, ViewChildren } from "@angular/core";
import { ClrDatagridFilter, ClrDatagridFilterInterface, ClrDateContainer } from "@clr/angular";
import { Observable, Subject } from "rxjs";

// @formatter:off
@Component({
    standalone: false,
    selector: 'dg-single-date-filter',
    templateUrl: './datagrid-single-date-filter.component.html',
    styleUrls: ['./datagrid-single-date-filter.component.scss']
})
// @formatter:on
export class DatagridSingleDateFilter<T> implements ClrDatagridFilterInterface<T>, OnDestroy {
    @ViewChild('input', { read: ElementRef })
    private inputRef: ElementRef;

    @ViewChildren(ClrDateContainer)
    private dateContainerQuery: QueryList<ClrDateContainer>;

    // The filter should return pure date (with no time at the end) when its state is taken
    @Input()
    public dateOnly: boolean;

    @Input()
    public filterProperty: string;

    @Input()
    public set clrFilterValue(value: Date) {
        this.date = value ? new Date(value) : null;
    }
    public get clrFilterValue(): string {
        return this.date ? new Date(this.date).toISOString() : null;
    }

    @Output()
    clrFilterValueChange: EventEmitter<any> = new EventEmitter();

    private changesSubject: Subject<T> = new Subject<T>();
    private destroySubject: Subject<void> = new Subject<void>();

    public date: Date;

    constructor(protected filterContainer: ClrDatagridFilter) {
        filterContainer.setFilter(this);
    }

    onDateChange(date: Date): void {
        this.date = date ? new Date(date) : null;
        this.changesSubject.next(null);
        this.clrFilterValueChange.emit(this.date);
    }

    /**
     * Indicates if the filter is currently active, (at least one input is set)
     */
    public isActive(): boolean {
        return (this.date != null);
    }

    /**
     * The method is not used for server-driven datagrids, so no need to implement it.
     */
    public accepts(_item: T): boolean {
        return false;
    }

    /**
     * The Observable required as part of the Filter interface
     */
    public get changes(): Observable<T> {
        return this.changesSubject.asObservable();
    }

    public get state(): any {
        const isoDate = new Date(this.date).toISOString();
        return {
            property: this.filterProperty,
            value: this.date
                ?  this.dateOnly ? [isoDate.slice(0,10)] : [isoDate]
                : null
        }
    }

    public ngOnDestroy(): void {
        this.destroySubject.next();
        this.destroySubject.complete();
    }

}
